import * as Dialog from '@radix-ui/react-dialog';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { LoaderIcon } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { saveFile, setFileName } from 'store/slices/imageToImage';
import { useMutation } from '@tanstack/react-query';
import { ImageToImageApis } from '../../services/imageApis';
import AuthRequiredModal from './AuthRequiredModal';

type ButtonType = 'default' | 'dropdown';

interface SaveImageModalProps {
  type?: ButtonType;
}

export default function SaveImageModal({ type }: SaveImageModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { fileName, generatedImageUrl, selectedStyle } = useSelector(
    (state: RootState) => state.imageToImage
  );
  const dispatch = useDispatch();

  const { mutate, isPending } = useMutation({
    mutationKey: ['save-image-to-image'],
    mutationFn: ImageToImageApis.saveGeneratedImage,
    onSuccess: () => {
      dispatch(saveFile({ fileSaved: true }));
      setIsOpen(false);
      toast.success('Image saved successfully!', {
        position: 'bottom-center',
      });
    },
    onError: () => {
      toast.error('Failed to save image. Please try again.', {
        position: 'bottom-center',
      });
    },
  });

  const handleSaveClick = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    setIsOpen(true);
  };

  const handleSave = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    mutate({
      image_name: fileName,
      image_url: generatedImageUrl,
      tool_type: selectedStyle,
    });
  };

  const renderTriggerButton = () => {
    if (type === 'default') {
      return (
        <Button
          variant="customPrimary"
          size="sm"
          onClick={handleSaveClick}
        >
          Save
        </Button>
      );
    }

    return (
      <button
        onClick={handleSaveClick}
        className="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 w-full"
      >
        Save Image
      </button>
    );
  };

  return (
    <>
      {renderTriggerButton()}

      <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50" />
          <Dialog.Content className="fixed text-text-primary left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-background p-6 shadow-lg w-[400px] max-w-[calc(100%-32px)]">
            <div className="flex flex-col gap-4">
              <Dialog.Title className="text-2xl font-bold text-primary">
                Save Image
              </Dialog.Title>

              <div className="space-y-2">
                <label className="text-sm">File name</label>
                <Input
                  value={fileName}
                  onChange={(e) =>
                    dispatch(setFileName({ fileName: e.target.value }))
                  }
                />
              </div>

              <div className="flex justify-end gap-2 mt-2">
                <Button
                  onClick={() => setIsOpen(false)}
                  variant="customSecondary"
                  size="sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  variant="customPrimary"
                  size="sm"
                  disabled={isPending}
                >
                  {isPending && (
                    <LoaderIcon className="w-4 h-4 animate-spin mr-2" />
                  )}
                  {isPending ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <AuthRequiredModal
        isOpen={showAuthModal}
        onOpenChange={setShowAuthModal}
        type="save"
      />
    </>
  );
} 